import * as React from "react";
import { useState } from "react";

import axios from "axios";

import {
  Button,
  CircularProgress,
  TextField,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControlLabel,
  Switch,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import {
  Notification,
  useNotify,
} from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import HistoryIcon from '@material-ui/icons/History';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    padding: "3em 2em",
  },
  container: {
    width: "100%",
    maxWidth: "900px",
  },
  pageTitle: {
    marginBottom: "0.5em",
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  pageSubtitle: {
    marginBottom: "2em",
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.95rem",
  },
  formCard: {
    marginBottom: "2em",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    borderRadius: "12px",
  },
  formContent: {
    padding: "2em",
  },
  formField: {
    marginBottom: "1.5em",
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
  dateInputs: {
    display: "flex",
    gap: "1em",
    marginBottom: "1.5em",
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "1.5em",
    padding: "1em",
    backgroundColor: "#fff3e0",
    borderRadius: "8px",
    border: "1px solid #ffb74d",
  },
  switchLabel: {
    marginLeft: "0.5em",
    color: "#e65100",
    fontWeight: 500,
  },
  button: {
    padding: "12px 32px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(63, 81, 181, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(63, 81, 181, 0.4)",
      transform: "translateY(-2px)",
    },
  },
  resultCard: {
    marginBottom: "2em",
    borderRadius: "12px",
    overflow: "hidden",
  },
  successCard: {
    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
  },
  dryRunCard: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  resultHeader: {
    padding: "1.5em 2em",
    color: "white",
    display: "flex",
    alignItems: "center",
    gap: "0.75em",
  },
  resultIcon: {
    fontSize: "2rem",
  },
  resultContent: {
    padding: "2em",
    backgroundColor: "white",
  },
  statGrid: {
    marginBottom: "1.5em",
  },
  statCard: {
    padding: "1.5em",
    textAlign: "center",
    borderRadius: "8px",
    backgroundColor: "#f5f7fa",
    height: "100%",
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: "0.5em",
  },
  refsSection: {
    marginTop: "1.5em",
    padding: "1em",
    backgroundColor: "#f5f7fa",
    borderRadius: "8px",
  },
  refsSectionTitle: {
    fontWeight: 600,
    marginBottom: "0.75em",
    display: "flex",
    alignItems: "center",
    gap: "0.5em",
  },
  refChip: {
    margin: "0.25em",
    fontFamily: "monospace",
    fontSize: "0.75rem",
  },
  infoBox: {
    marginTop: "1.5em",
    padding: "1em",
    backgroundColor: "#e3f2fd",
    borderRadius: "8px",
    border: "1px solid #90caf9",
    display: "flex",
    alignItems: "flex-start",
    gap: "0.75em",
  },
  infoIcon: {
    color: "#1976d2",
    marginTop: "0.1em",
  },
  warningBox: {
    padding: "1em",
    backgroundColor: "#fff3e0",
    borderRadius: "8px",
    border: "1px solid #ffb74d",
    marginBottom: "1.5em",
  },
  warningText: {
    color: "#e65100",
    fontSize: "0.9rem",
  },
  dateRange: {
    marginTop: "0.5em",
    padding: "0.75em 1em",
    backgroundColor: "rgba(255,255,255,0.2)",
    borderRadius: "6px",
    fontSize: "0.9rem",
  },
}));

interface FetchResult {
  status: string;
  message: string;
  from_date: string;
  to_date: string;
  total_transactions: number;
  transactions_to_insert: number;
  transactions_skipped: number;
  dry_run: boolean;
  sample_new_refs?: string[];
  sample_existing_refs?: string[];
}

const RewardsEwalletHistoryFetch = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState<FetchResult | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!fromDate || !toDate) {
      notify("Please enter both from and to dates", "error", "", undefined, 5000);
      return;
    }

    const from = new Date(fromDate);
    const to = new Date(toDate);

    if (to < from) {
      notify("To date must be after from date", "error", "", undefined, 5000);
      return;
    }

    const daysDiff = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 7) {
      notify(`Date range cannot exceed 7 days. Current range: ${daysDiff} days`, "error", "", undefined, 5000);
      return;
    }

    setLoading(true);
    setResult(null);

    const params: any = {
      from_date: fromDate,
      to_date: toDate,
    };
    
    if (dryRun) {
      params.dry_run = true;
    }
    
    const config = getConfigGet(params);

    axios
      .get(ENDPOINTS.REWARDS_EWALLET_HISTORY_FETCH, config)
      .then((res: any) => {
        setLoading(false);
        setResult(res.data);
        if (res.data.dry_run) {
          notify("Dry run completed - no data was inserted", "info", "", undefined, 5000);
        } else {
          notify(`Successfully processed ${res.data.transactions_to_insert} transactions`, "info", "", undefined, 5000);
        }
      })
      .catch((error) => {
        setLoading(false);
        notify(
          error?.response?.data?.message || "Failed to fetch rewards ewallet history",
          "error",
          "",
          undefined,
          5000
        );
      });
  };

  return (
    <div className={classes.main}>
      <div className={classes.container}>
        <Typography variant="h4" className={classes.pageTitle}>
          Rewards E-Wallet History Fetch
        </Typography>
        <Typography className={classes.pageSubtitle}>
          Fetch and sync PPS rewards e-wallet transaction history to the database
        </Typography>

        <Card className={classes.formCard}>
          <CardContent className={classes.formContent}>
            <Box className={classes.warningBox}>
              <Typography className={classes.warningText}>
                <strong>Important:</strong> This tool fetches transactions from the PPS rewards e-wallet and inserts missing records into the database. 
                Date range is limited to 7 days maximum to prevent timeout issues.
              </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="From Date"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.formField}
                    InputLabelProps={{ shrink: true }}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="To Date"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    fullWidth
                    variant="outlined"
                    className={classes.formField}
                    InputLabelProps={{ shrink: true }}
                    disabled={loading}
                  />
                </Grid>
              </Grid>

              <Box className={classes.switchContainer}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dryRun}
                      onChange={(e) => setDryRun(e.target.checked)}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label=""
                />
                <Typography className={classes.switchLabel}>
                  Dry Run Mode - Preview what would be inserted without making changes
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading || !fromDate || !toDate}
                className={classes.button}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <HistoryIcon />}
              >
                {loading ? "Fetching..." : dryRun ? "Preview Transactions" : "Fetch & Insert Transactions"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {result && (
          <Paper 
            className={`${classes.resultCard} ${result.dry_run ? classes.dryRunCard : classes.successCard}`}
            elevation={3}
          >
            <div className={classes.resultHeader}>
              {result.dry_run ? (
                <WarningIcon className={classes.resultIcon} />
              ) : (
                <CheckCircleIcon className={classes.resultIcon} />
              )}
              <div>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  {result.dry_run ? "Dry Run Complete" : "Sync Complete"}
                </Typography>
                <Typography variant="body2" className={classes.dateRange}>
                  {result.from_date} → {result.to_date}
                </Typography>
              </div>
            </div>

            <div className={classes.resultContent}>
              <Grid container spacing={2} className={classes.statGrid}>
                <Grid item xs={12} sm={4}>
                  <Box className={classes.statCard}>
                    <Typography className={classes.statValue}>
                      {result.total_transactions}
                    </Typography>
                    <Typography className={classes.statLabel}>
                      Total Transactions Found
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className={classes.statCard} style={{ backgroundColor: "#e8f5e9" }}>
                    <Typography className={classes.statValue} style={{ color: "#2e7d32" }}>
                      {result.transactions_to_insert}
                    </Typography>
                    <Typography className={classes.statLabel}>
                      {result.dry_run ? "New (Would Insert)" : "Inserted"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className={classes.statCard} style={{ backgroundColor: "#fff3e0" }}>
                    <Typography className={classes.statValue} style={{ color: "#e65100" }}>
                      {result.transactions_skipped}
                    </Typography>
                    <Typography className={classes.statLabel}>
                      Skipped (Already Exist)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {result.sample_new_refs && result.sample_new_refs.length > 0 && (
                <Box className={classes.refsSection}>
                  <Typography className={classes.refsSectionTitle} style={{ color: "#2e7d32" }}>
                    <CheckCircleIcon fontSize="small" />
                    Sample New Transaction References (first 10)
                  </Typography>
                  {result.sample_new_refs.map((ref, index) => (
                    <Chip
                      key={index}
                      label={ref}
                      size="small"
                      className={classes.refChip}
                      style={{ backgroundColor: "#e8f5e9" }}
                    />
                  ))}
                </Box>
              )}

              {result.sample_existing_refs && result.sample_existing_refs.length > 0 && (
                <Box className={classes.refsSection}>
                  <Typography className={classes.refsSectionTitle} style={{ color: "#e65100" }}>
                    <WarningIcon fontSize="small" />
                    Sample Existing Transaction References (first 10)
                  </Typography>
                  {result.sample_existing_refs.map((ref, index) => (
                    <Chip
                      key={index}
                      label={ref}
                      size="small"
                      className={classes.refChip}
                      style={{ backgroundColor: "#fff3e0" }}
                    />
                  ))}
                </Box>
              )}

              {result.dry_run && (
                <Box className={classes.infoBox}>
                  <InfoIcon className={classes.infoIcon} />
                  <Typography variant="body2">
                    This was a dry run. No data was inserted into the database. 
                    To actually sync the transactions, turn off "Dry Run Mode" and run again.
                  </Typography>
                </Box>
              )}
            </div>
          </Paper>
        )}

        <Notification />
      </div>
    </div>
  );
};

export default RewardsEwalletHistoryFetch;
